(() => {
  'use strict';

  const ROOT_SELECTOR = '.rebuy-bundle-builder';
  const CARD_SELECTOR = '.rebuy-bundle-builder__step-container .rebuy-product-block';
  const META_SECTION_ID = 'rebuy-bundle-product-meta';

  const metaCache = new Map();
  const rootStates = new WeakMap();
  const cardTasks = new WeakMap();
  const variantSelects = new WeakSet();

  const EMPTY_META = Object.freeze({
    handle: '',
    shortDescription: '',
    badgeUrl: '',
    desktopPurple: '',
    desktopGreen: '',
    mobilePurple: '',
    mobileGreen: ''
  });

  const escapeHtml = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const normalizeHandle = (value) => {
    let handle = String(value || '').trim();
    try {
      handle = decodeURIComponent(handle);
    } catch (_) {
      // Keep the original value if it is not URI encoded.
    }

    return handle
      .toLowerCase()
      .replace(/^\/+|\/+$/g, '')
      .replace(/[?#].*$/, '');
  };

  /**
   * Converts strings, lists, rich-text JSON and common metaobject/reference
   * shapes into displayable text.
   */
  const extractText = (value, seen = new WeakSet()) => {
    if (value == null) return '';

    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);

    if (Array.isArray(value)) {
      return value
        .map((item) => extractText(item, seen))
        .filter(Boolean)
        .join(' ')
        .trim();
    }

    if (typeof value !== 'object') return '';
    if (seen.has(value)) return '';
    seen.add(value);

    // Shopify rich-text metafield JSON.
    if (Array.isArray(value.children)) {
      const childText = value.children
        .map((child) => extractText(child, seen))
        .filter(Boolean)
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();

      if (childText) return childText;
    }

    // Storefront/metaobject style: { fields: [{ key, value }, ...] }.
    if (Array.isArray(value.fields)) {
      const preferredFieldKeys = ['text', 'label', 'title', 'name', 'value'];

      for (const preferredKey of preferredFieldKeys) {
        const field = value.fields.find((item) => item?.key === preferredKey);
        const fieldText = extractText(field?.value, seen);
        if (fieldText) return fieldText;
      }

      for (const field of value.fields) {
        const fieldText = extractText(field?.value, seen);
        if (fieldText) return fieldText;
      }
    }

    if (value.reference) {
      const referenceText = extractText(value.reference, seen);
      if (referenceText) return referenceText;
    }

    if (Array.isArray(value.references?.nodes)) {
      const referenceText = extractText(value.references.nodes, seen);
      if (referenceText) return referenceText;
    }

    const preferredKeys = ['text', 'label', 'title', 'name', 'value'];
    const structuralValues = new Set(['root', 'paragraph', 'text', 'heading', 'list', 'list-item']);

    for (const key of preferredKeys) {
      if (!(key in value)) continue;

      const candidate = extractText(value[key], seen);
      if (candidate && !structuralValues.has(candidate.toLowerCase())) return candidate;
    }

    // Last resort: inspect non-structural properties.
    for (const [key, nestedValue] of Object.entries(value)) {
      if (['type', 'id', 'admin_graphql_api_id', 'updated_at', 'created_at'].includes(key)) {
        continue;
      }

      const candidate = extractText(nestedValue, seen);
      if (candidate && !structuralValues.has(candidate.toLowerCase())) return candidate;
    }

    return '';
  };

  const getProductHandle = (card) => {
    const explicitHandle =
      card.dataset.productHandle ||
      card.querySelector('[data-product-handle]')?.dataset.productHandle;

    if (explicitHandle) return normalizeHandle(explicitHandle);

    const productLink = card.querySelector(
      '.rebuy-product-title[href*="/products/"], ' +
      '.rebuy-product-image[href*="/products/"], ' +
      'a[href*="/products/"]'
    );

    const href = productLink?.getAttribute('href');
    if (!href) return '';

    try {
      const url = new URL(href, window.location.origin);
      const match = url.pathname.match(/\/products\/([^/]+)/i);
      return match?.[1] ? normalizeHandle(match[1]) : '';
    } catch (_) {
      const match = href.match(/\/products\/([^/?#]+)/i);
      return match?.[1] ? normalizeHandle(match[1]) : '';
    }
  };

  const buildMetaUrl = (handle) => {
    const root = window.Shopify?.routes?.root || '/';
    const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
    const url = new URL(
      `${normalizedRoot}products/${encodeURIComponent(handle)}`,
      window.location.origin
    );

    url.searchParams.set('section_id', META_SECTION_ID);
    return url.toString();
  };

  const normalizeMeta = (raw) => ({
    handle: normalizeHandle(raw?.handle),
    shortDescription: extractText(raw?.shortDescription),
    badgeUrl: extractText(raw?.badgeUrl),
    desktopPurple: extractText(raw?.desktopPurple),
    desktopGreen: extractText(raw?.desktopGreen),
    mobilePurple: extractText(raw?.mobilePurple),
    mobileGreen: extractText(raw?.mobileGreen)
  });

  const fetchProductMeta = async (handle) => {
    const response = await fetch(buildMetaUrl(handle), {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        Accept: 'text/html',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (!response.ok) {
      throw new Error(`Metadata section request failed (${response.status}) for ${handle}`);
    }

    const html = await response.text();
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const jsonElement = documentFragment.querySelector('[data-rebuy-product-meta-json]');

    if (!jsonElement) {
      throw new Error(
        `Section "${META_SECTION_ID}" did not return a metadata JSON element for ${handle}`
      );
    }

    const raw = JSON.parse(jsonElement.textContent || '{}');
    return normalizeMeta(raw);
  };

  const getProductMeta = (handle) => {
    if (!metaCache.has(handle)) {
      const request = fetchProductMeta(handle).catch((error) => {
        console.warn('[Rebuy Bundle Builder customization]', error);
        return EMPTY_META;
      });

      metaCache.set(handle, request);
    }

    return metaCache.get(handle);
  };

  const buildBadgesHtml = (meta) => {
    const badges = [];

    if (meta.desktopPurple) {
      badges.push(`
        <div class="hidden--mobile product-app-card--rectangle app-card-tag-1">
          <span class="card-span-tag-1">${escapeHtml(meta.desktopPurple)}</span>
        </div>
      `);
    }

    if (meta.desktopGreen) {
      badges.push(`
        <div class="hidden--mobile product-app-card--rectangle app-card-tag-2">
          <span class="card-span-tag-2">${escapeHtml(meta.desktopGreen)}</span>
        </div>
      `);
    }

    if (meta.mobilePurple) {
      badges.push(`
        <div class="hidden--desktop product-app-card--rectangle app-card-tag-1">
          <span class="card-span-tag-1">${escapeHtml(meta.mobilePurple)}</span>
        </div>
      `);
    }

    if (meta.mobileGreen) {
      badges.push(`
        <div class="hidden--desktop product-app-card--rectangle app-card-tag-2">
          <span class="card-span-tag-2">${escapeHtml(meta.mobileGreen)}</span>
        </div>
      `);
    }

    return badges.join('').trim();
  };

  const renderBadges = (card, meta) => {
    const media = card.querySelector('.rebuy-product-media');
    if (!media) return;

    const badgeHtml = buildBadgesHtml(meta);
    const signature = [
      meta.desktopPurple,
      meta.desktopGreen,
      meta.mobilePurple,
      meta.mobileGreen
    ].join('|');

    let container = media.querySelector('.flt-rebuy-product-badges');

    if (!badgeHtml) {
      container?.remove();
      return;
    }

    if (!container) {
      container = document.createElement('div');
      container.className =
        'product-badges product-badges--top-left flt-rebuy-product-badges';
      media.prepend(container);
    }

    if (container.dataset.signature !== signature) {
      container.innerHTML = badgeHtml;
      container.dataset.signature = signature;
    }
  };

  const renderShortDescription = (card, meta) => {
    const info = card.querySelector('.rebuy-product-info');
    if (!info) return;

    let description = info.querySelector('.flt-rebuy-short-description');

    if (!meta.shortDescription) {
      description?.remove();
      return;
    }

    if (!description) {
      description = document.createElement('div');
      description.className =
        'product-short-description flt-rebuy-short-description';

      // Desired order: title, short description, variant buttons, reviews, price, CTA.
      // Keep the title and short description inside .rebuy-product-info.
      const directTitle = Array.from(info.children).find((child) =>
        child.matches('.rebuy-product-title')
      );

      if (directTitle) {
        directTitle.insertAdjacentElement('afterend', description);
      } else {
        info.prepend(description);
      }
    }

    if (description.dataset.signature !== meta.shortDescription) {
      description.innerHTML = `<p>${escapeHtml(meta.shortDescription)}</p>`;
      description.dataset.signature = meta.shortDescription;
    }
  };

  const getDirectChild = (parent, selector) =>
    Array.from(parent?.children || []).find((child) => child.matches(selector)) || null;

  const syncVariantButtons = (select, buttonsContainer) => {
    const selectedValue = String(select.value || '');

    buttonsContainer
      .querySelectorAll('.flt-rebuy-variant-button')
      .forEach((button) => {
        const selected = button.dataset.value === selectedValue;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-checked', selected ? 'true' : 'false');
      });
  };

  const renderVariantButtons = (card) => {
    const product = card.querySelector('.rebuy-bundle-builder__step-product');
    if (!product) return;

    const options =
      getDirectChild(product, '.rebuy-product-options') ||
      product.querySelector('.rebuy-product-options');
    const select = options?.querySelector('select.rebuy-select, select');

    if (!options || !select || select.options.length < 1) return;

    select.classList.add('flt-rebuy-native-select');
    select.setAttribute('aria-hidden', 'true');
    select.tabIndex = -1;

    let buttonsContainer = options.querySelector('.flt-rebuy-variant-buttons');
    if (!buttonsContainer) {
      buttonsContainer = document.createElement('div');
      buttonsContainer.className = 'flt-rebuy-variant-buttons';
      buttonsContainer.setAttribute('role', 'radiogroup');
      buttonsContainer.setAttribute(
        'aria-label',
        select.getAttribute('aria-label') || 'Choose a product variant'
      );
      options.append(buttonsContainer);
    }

    const optionData = Array.from(select.options).map((option) => ({
      value: String(option.value),
      label: option.textContent.trim(),
      disabled: option.disabled
    }));

    const signature = JSON.stringify(optionData);

    if (buttonsContainer.dataset.signature !== signature) {
      buttonsContainer.replaceChildren();

      optionData.forEach(({ value, label, disabled }) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'flt-rebuy-variant-button';
        button.dataset.value = value;
        button.textContent = label;
        button.disabled = disabled;
        button.setAttribute('role', 'radio');
        button.setAttribute('aria-checked', 'false');
        button.setAttribute('aria-label', `Select ${label}`);

        button.addEventListener('click', () => {
          if (button.disabled) return;

          select.value = value;

          // Rebuy's Bundle Builder select uses v-model plus a change handler.
          // Dispatch both events so its selected variant, image and price update.
          select.dispatchEvent(new Event('input', { bubbles: true }));
          select.dispatchEvent(new Event('change', { bubbles: true }));

          syncVariantButtons(select, buttonsContainer);

          requestAnimationFrame(() => {
            syncVariantButtons(select, buttonsContainer);
            reorderProductElements(card);
          });
        });

        buttonsContainer.append(button);
      });

      buttonsContainer.dataset.signature = signature;
    }

    if (!variantSelects.has(select)) {
      select.addEventListener('change', () => {
        syncVariantButtons(select, buttonsContainer);
        requestAnimationFrame(() => reorderProductElements(card));
      });
      variantSelects.add(select);
    }

    syncVariantButtons(select, buttonsContainer);
  };

  function reorderProductElements(card) {
    const product = card.querySelector('.rebuy-bundle-builder__step-product');
    if (!product) return;

    const info =
      getDirectChild(product, '.rebuy-product-info') ||
      product.querySelector('.rebuy-product-info');
    const options =
      getDirectChild(product, '.rebuy-product-options') ||
      product.querySelector('.rebuy-product-options');
    const actions =
      getDirectChild(
        product,
        '.rebuy-bundle-builder__product-actions, .rebuy-product-actions'
      ) ||
      product.querySelector(
        '.rebuy-bundle-builder__product-actions, .rebuy-product-actions'
      );

    const review =
      info?.querySelector('.rebuy-product-review') ||
      getDirectChild(product, '.rebuy-product-review');
    const price =
      info?.querySelector('.rebuy-product-price') ||
      getDirectChild(product, '.rebuy-product-price');

    product.classList.add('flt-rebuy-layout-enhanced');

    // Move reviews below the variant buttons, matching the target product card.
    if (review) {
      review.classList.add('flt-rebuy-review');

      if (options) {
        if (options.nextElementSibling !== review) {
          options.insertAdjacentElement('afterend', review);
        }
      } else if (actions && review.nextElementSibling !== actions) {
        actions.insertAdjacentElement('beforebegin', review);
      }
    }

    // Always put price immediately above the Add to Bundle/Add to Cart area.
    // Do not place it merely after the option selector because Rebuy's layout
    // can visually position that node at the top of the card.
    if (price) {
      price.classList.add('flt-rebuy-price');

      if (actions && price.nextElementSibling !== actions) {
        actions.insertAdjacentElement('beforebegin', price);
      } else if (!actions && review && review.nextElementSibling !== price) {
        review.insertAdjacentElement('afterend', price);
      } else if (!actions && options && options.nextElementSibling !== price) {
        options.insertAdjacentElement('afterend', price);
      }
    }
  }

  const enhanceCard = (card) => {
    if (!card.querySelector('.rebuy-bundle-builder__step-product')) return;

    // Reordering does not depend on metafield data, so do it immediately.
    reorderProductElements(card);
    renderVariantButtons(card);

    const handle = getProductHandle(card);
    if (!handle) return;

    if (cardTasks.get(card) === handle) return;
    cardTasks.set(card, handle);

    getProductMeta(handle)
      .then((meta) => {
        // Rebuy can recycle/re-render product cards. Verify the card still
        // represents the same product before modifying it.
        if (!card.isConnected || getProductHandle(card) !== handle) return;

        renderBadges(card, meta);
        renderShortDescription(card, meta);
        renderVariantButtons(card);
        reorderProductElements(card);
        card.dataset.fltRebuyMetaHandle = handle;
      })
      .finally(() => {
        if (cardTasks.get(card) === handle) {
          cardTasks.delete(card);
        }
      });
  };

  const scanRoot = (root) => {
    root.querySelectorAll(CARD_SELECTOR).forEach(enhanceCard);
  };

  const scheduleScan = (root) => {
    const state = rootStates.get(root);
    if (!state || state.scanScheduled) return;

    state.scanScheduled = true;

    requestAnimationFrame(() => {
      state.scanScheduled = false;
      scanRoot(root);
    });
  };

  const attachToRoot = (root) => {
    if (!(root instanceof Element) || rootStates.has(root)) return;

    const state = {
      scanScheduled: false,
      observer: null
    };

    state.observer = new MutationObserver(() => scheduleScan(root));
    state.observer.observe(root, {
      childList: true,
      subtree: true
    });

    rootStates.set(root, state);
    scheduleScan(root);
  };

  const discoverRoots = (scope = document) => {
    if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) {
      attachToRoot(scope);
    }

    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(attachToRoot);
  };

  const rootFromRebuyEvent = (event) => {
    const detail = event?.detail;
    const instance =
      detail?.widget ||
      detail?.bundleBuilder ||
      detail?.bundle_builder ||
      detail;

    const element =
      instance?.el ||
      instance?.node ||
      instance?.element ||
      instance?.$el;

    if (!(element instanceof Element)) return null;
    if (element.matches(ROOT_SELECTOR)) return element;
    return element.querySelector(ROOT_SELECTOR);
  };

  document.addEventListener('rebuy.ready', (event) => {
    const eventRoot = rootFromRebuyEvent(event);
    if (eventRoot) attachToRoot(eventRoot);

    // Some Rebuy builds do not expose the DOM element on event.detail.
    discoverRoots(document);
  });

  // Bundle Builder changes can replace/recreate product cards. Its own
  // observer handles those changes after a root has been discovered.
  const discoveryObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof Element) discoverRoots(node);
      }
    }
  });

  const start = () => {
    discoverRoots(document);
    discoveryObserver.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();